/**
 * This class is a wrapper for a promise which waits for a timeout or abort on the signal.
 * The promise stored in the class can be obtained through getPromise function.
 * The constructor accepts the waitTime (in milliseconds) and an optional AbortSignal.
 *    When the signal is aborted, the promise is rejected.
 *    When the waitTime is met, the promise will be resolved.
 * The cancel function provides option to cancel the timeout and prevent rejection upon abort.
 * After cancel, once the reference to this object is removed then GarbageCollector will free the promise.
 *
 * Note: In signal abort cases the promise rejection doesn't return the signal reason.
 *  Reasons:
 *    1. Even though AbortSignal.reason is introduced in node 17.2.0, the node version 18.12.1 still doesn't support plumbing of reason from AbortController to AbortSignal.
 *    2. AbortSignal.reason was introduced in chrome/edge -98, safari 15.4.
 *    3. Relying on the reason of AbortSignal for business logic will be erroneous.
 *    4. The previous implementation of setTimeoutPromise was providing a AbortError message created by our module, unit tests were passing because of this reason, this creates more confusion.
 */
export class CancellableWait {
    private promise: Promise<void>;
    private promiseResolve: any;
    private promiseReject: any;
    private onAbortFunc: any;
    private timerId: number = 0;
    private abortSignal: AbortSignal | undefined;
    private timedOut: boolean = false;
    private aborted: boolean = false;

    constructor(waitTimeInMS: number, signal?: AbortSignal) {
        this.promise = new Promise((resolve, reject) => {
            this.promiseResolve = resolve;
            this.promiseReject = reject;
        });

        if (signal?.aborted) {
            // signal is already aborted, just reject the promise, nothing else required.
            // Note: If users attach the handlers to the promise after getting promise from getPromise() then catch/reject handler will get executed.
            this.aborted = true;
            this.promiseReject();
        } else {
            this.abortSignal = signal;
            this.onAbortFunc = this.onAbort.bind(this);
            this.abortSignal?.addEventListener("abort", this.onAbortFunc);
            this.timerId = window.setTimeout(this.onTimeout.bind(this), waitTimeInMS);
        }
    }

    /** Cancels the promise, the promise will never resolve or reject after this call.
     *  If the promise needs to be rejected then perform abort on the signal passed in constructor instead of calling this function.
     *  This is a no op function if promise was already resolved/rejected. */
    public cancel(): void {
        this.abortSignal?.removeEventListener("abort", this.onAbortFunc);
        if (this.timerId) {
            window.clearTimeout(this.timerId);
            this.timerId = 0;
        }
        this.abortSignal = undefined;
    }

    /** Returns the actual promise which can be used in promise race or for adding .then/.catch handlers. */
    public getPromise(): Promise<void> {
        return this.promise;
    }

    /** Returns true if wait timedout and promise was resolved. */
    public isTimedOut(): boolean {
        return this.timedOut;
    }

    /** Returns true if signal was aborted and promise was rejected.
     *  Note: If cancel was invoked before signal abort, then this value will be false.
     */
    public isAborted(): boolean {
        return this.aborted;
    }

    private onAbort(): void {
        this.aborted = true;
        this.promiseReject();
        this.cancel();
    }

    private onTimeout(): void {
        this.timedOut = true;
        this.promiseResolve();
        this.timerId = 0;
        this.cancel();
    }
}
